import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Button,
  Modal,
  Space,
  message,
  Card,
  Tag,
  Popconfirm,
  Switch,
  Tooltip,
} from "antd";
import {
  Plus,
  RefreshCw,
  Edit,
  Trash2,
  Send,
  Mail,
  MessageSquare,
  Webhook,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import apiService from "../../services/api";
import {
  setChannels,
  addChannel,
  updateChannel,
  removeChannel,
  setLoading,
  setError,
  selectChannels,
  selectNotificationLoading,
} from "../../store/notificationSlice";
import ChannelForm from "./ChannelForm";
import { formatDateTime } from "../../utils/formatters";

const ChannelList = () => {
  const dispatch = useDispatch();
  const channels = useSelector(selectChannels);
  const loading = useSelector(selectNotificationLoading);
  const [showModal, setShowModal] = useState(false);
  const [currentChannel, setCurrentChannel] = useState(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const loadChannels = useCallback(async () => {
    dispatch(setLoading(true));
    try {
      const params = {
        page: pagination.current,
        page_size: pagination.pageSize,
      };

      const response = await apiService.notificationChannels.getChannels(params);
      if (!response.data.success) {
        throw new Error(response.data.message || "获取通知渠道失败");
      }

      dispatch(setChannels(response.data.data?.list || []));
      setPagination((prev) => ({
        ...prev,
        total: response.data.data?.total || 0,
      }));
    } catch (err) {
      message.error(err.message || "获取通知渠道失败");
      dispatch(setError(err.message));
    }
  }, [dispatch, pagination.current, pagination.pageSize]);

  useEffect(() => {
    loadChannels();
  }, [loadChannels]);

  const handleCreate = () => {
    setCurrentChannel(null);
    setShowModal(true);
  };

  const handleEdit = (record) => {
    setCurrentChannel(record);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await apiService.notificationChannels.deleteChannel(id);
      if (!response.data.success) {
        throw new Error(response.data.message || "删除失败");
      }
      message.success("删除成功");
      dispatch(removeChannel(id));
    } catch (err) {
      message.error(err.message || "删除失败");
    }
  };

  const handleToggle = async (id, enabled) => {
    try {
      const response = await apiService.notificationChannels.toggleChannel(id);
      if (!response.data.success) {
        throw new Error(response.data.message || "切换状态失败");
      }
      message.success(enabled ? "已禁用" : "已启用");
      dispatch(updateChannel(response.data.data));
    } catch (err) {
      message.error(err.message || "切换状态失败");
    }
  };

  const handleTest = async (id) => {
    try {
      const response = await apiService.notificationChannels.testChannel(id);
      if (!response.data.success) {
        throw new Error(response.data.message || "测试失败");
      }
      message.success("测试消息已发送");
    } catch (err) {
      message.error(err.message || "测试失败");
    }
  };

  const handleFormSubmit = async (values) => {
    try {
      if (currentChannel) {
        const response = await apiService.notificationChannels.updateChannel(
          currentChannel.id,
          values
        );
        if (!response.data.success) {
          throw new Error(response.data.message || "更新失败");
        }
        message.success("更新成功");
        dispatch(updateChannel(response.data.data));
      } else {
        const response = await apiService.notificationChannels.createChannel(values);
        if (!response.data.success) {
          throw new Error(response.data.message || "创建失败");
        }
        message.success("创建成功");
        dispatch(addChannel(response.data.data));
      }
      setShowModal(false);
      loadChannels();
    } catch (err) {
      message.error(err.message || "操作失败");
    }
  };

  const getTypeIcon = (type) => {
    const icons = {
      feishu: <MessageSquare size={16} />,
      email: <Mail size={16} />,
      webhook: <Webhook size={16} />,
    };
    return icons[type] || <MessageSquare size={16} />;
  };

  const getTypeLabel = (type) => {
    const labels = {
      feishu: "飞书",
      email: "邮件",
      webhook: "Webhook",
    };
    return labels[type] || type;
  };

  const columns = [
    {
      title: "渠道名称",
      dataIndex: "name",
      key: "name",
      width: 200,
    },
    {
      title: "类型",
      dataIndex: "type",
      key: "type",
      width: 120,
      render: (type) => (
        <Space>
          {getTypeIcon(type)}
          {getTypeLabel(type)}
        </Space>
      ),
    },
    {
      title: "状态",
      dataIndex: "enabled",
      key: "enabled",
      width: 100,
      render: (enabled) => (
        <Tag color={enabled ? "green" : "default"}>
          {enabled ? "启用" : "禁用"}
        </Tag>
      ),
    },
    {
      title: "创建时间",
      dataIndex: "created_at",
      key: "created_at",
      width: 180,
      render: (text) => formatDateTime(text),
    },
    {
      title: "操作",
      key: "action",
      width: 250,
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="测试发送">
            <Button
              type="link"
              size="small"
              icon={<Send size={16} />}
              onClick={() => handleTest(record.id)}
            />
          </Tooltip>
          <Switch
            checked={record.enabled}
            onChange={() => handleToggle(record.id, record.enabled)}
            checkedChildren="启用"
            unCheckedChildren="禁用"
          />
          <Tooltip title="编辑">
            <Button
              type="link"
              size="small"
              icon={<Edit size={16} />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="确定要删除这个渠道吗?"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除">
              <Button
                type="link"
                size="small"
                danger
                icon={<Trash2 size={16} />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Button
              type="primary"
              icon={<Plus size={16} />}
              onClick={handleCreate}
            >
              新建渠道
            </Button>
            <Button icon={<RefreshCw size={16} />} onClick={loadChannels}>
              刷新
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={channels}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => {
              setPagination((prev) => ({ ...prev, current: page, pageSize }));
            },
          }}
        />
      </Card>

      <Modal
        title={currentChannel ? "编辑通知渠道" : "新建通知渠道"}
        open={showModal}
        onCancel={() => setShowModal(false)}
        footer={null}
        width={600}
        destroyOnClose
      >
        <ChannelForm
          initialValues={currentChannel}
          onSubmit={handleFormSubmit}
          onCancel={() => setShowModal(false)}
        />
      </Modal>
    </div>
  );
};

export default ChannelList;
